import { authService } from "../../services/auth";
import { tokenStorage } from "../../utils/tokenStorage";
import { hasPermission } from "../../utils/access";

const state = {
  user: null,
  accessToken: null,
  refreshToken: null,
  remember: false,
  loading: false,
};

const getters = {
  isAuthenticated: (state) => Boolean(state.accessToken),
  user: (state) => state.user,
  loading: (state) => state.loading,
  hasPermission: (state) => (code) => hasPermission(state.user, code),
};

const mutations = {
  setLoading(state, value) {
    state.loading = value;
  },
  setSession(state, session) {
    state.user = session.user || null;
    state.accessToken = session.accessToken || null;
    state.refreshToken = session.refreshToken || null;
    state.remember = Boolean(session.remember);
  },
  clearSession(state) {
    state.user = null;
    state.accessToken = null;
    state.refreshToken = null;
    state.remember = false;
  },
};

const actions = {
  async login({ commit }, { email, password, remember }) {
    commit("setLoading", true);
    try {
      const response = await authService.login({ email, password, remember: Boolean(remember) });
      const data = response.data.data;
      const session = {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      };
      tokenStorage.saveSession(session, Boolean(remember));
      commit("setSession", { ...session, remember: Boolean(remember) });
      return session;
    } finally {
      commit("setLoading", false);
    }
  },

  async refresh({ commit, state }) {
    const session = tokenStorage.getSession();
    if (!session || !session.refreshToken) {
      throw new Error("No hay token de refresco disponible");
    }

    const response = await authService.refresh({
      refreshToken: session.refreshToken,
      remember: session.remember || state.remember || false,
    });

    const data = response.data.data;
    const updated = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user || session.user,
    };

    tokenStorage.saveSession(updated, session.remember || state.remember);
    commit("setSession", { ...updated, remember: session.remember || state.remember });
    return updated;
  },

  async logout({ commit }) {
    const session = tokenStorage.getSession();
    try {
      if (session && session.refreshToken) {
        await authService.logout({ refreshToken: session.refreshToken });
      }
    } catch (err) {
      // ignore server logout errors
    } finally {
      tokenStorage.clear();
      commit("clearSession");
    }
  },

  restoreSession({ commit }) {
    const session = tokenStorage.getSession();
    if (session && session.accessToken) {
      commit("setSession", session);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
